import type { JSX, ReactNode } from 'react';
import type { WireSourceField } from '@logistics/contracts';
import { cn } from '@/lib/utils';
import { fieldLabel, input } from '../shell/look';
import { Switch } from '../shell/switch';
import { SelectField } from './select-field';

/**
 * One row of a plugin's form, rendered from what the server said it needs.
 * Nothing here knows what Samsara is — add a plugin and its form appears.
 */
export function SourceField({
  field,
  value,
  configured,
  onChange,
}: {
  field: WireSourceField;
  value: string;
  /** A stored credential: the box stays empty, and empty means "keep it". */
  configured: boolean;
  onChange: (value: string) => void;
}): JSX.Element {
  const id = `field-${field.name}`;
  const label = field.required ? `${field.label} *` : field.label;

  if (field.type === 'boolean') {
    return (
      <div className="flex items-center justify-between gap-3 text-[12.5px]">
        <label htmlFor={id} className="text-mute">
          {field.label}
        </label>
        <Switch
          id={id}
          label={field.label}
          on={value === 'true'}
          onChange={(on) => onChange(String(on))}
        />
      </div>
    );
  }

  let control: ReactNode;
  if (field.type === 'select') {
    control = <SelectField id={id} field={field} value={value} onChange={onChange} />;
  } else {
    control = (
      <input
        id={id}
        value={value}
        type={field.secret ? 'password' : field.type === 'number' ? 'number' : 'text'}
        inputMode={field.type === 'number' ? 'numeric' : undefined}
        autoComplete="off"
        placeholder={configured ? '••••••••  set — leave empty to keep' : field.placeholder}
        className={cn(input, 'font-mono text-[13.5px]')}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <label htmlFor={id} className={fieldLabel}>
      {label}
      {control}
    </label>
  );
}
